import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Subject, Observable, BehaviorSubject, Subscription } from 'rxjs';
import { tap, map, timeout } from 'rxjs/operators';
import * as FileSaver from 'file-saver';

import { environment } from '@environments/environment';
import { Order } from '@classes/order/order';
import { UiService } from './ui.service';
import { Pagination } from '@classes/pagination/pagination';
import { PaginationRequest } from '@classes/pagination/pagination-request';

import * as moment from 'moment';
import { User } from '@classes/user/user';
import { Site } from '@classes/site';
import { Counter } from '@classes/order/counter';
import { InternalComment } from '@classes/order/internalComment';
import { OrderStepTemperature } from '@classes/order/orderStepTemperature';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  ordersListChanged = new Subject<Order[]>();
  selectedOrderChanged = new Subject<Order>();
  counterTriggered = new Subject<boolean>();

  paginationSubject = new BehaviorSubject<Pagination>(null);
  unassignedToTreatSubject = new BehaviorSubject<number>(0);
  assignedToTreatSubject = new BehaviorSubject<number>(0);

  dateNow: Date;

  private baseUrl: string = environment.apiUrl + 'orders';
  private orders: Order[];
  private lastQuery: string;
  private lastQueryObj: {
    name: null;
    template: null;
    status: null;
    user: null;
    client: null;
    fromStartDate: null;
    fromCreatedOn: null;
    fromModifiedOn: null;
    createdBy: null;
  };
  private getListSub: Subscription;
  private selectedOrder: Order;

  constructor(
    private http: HttpClient,
    private uiSrv: UiService,
  ) {}

  getList(
    request: PaginationRequest,
    isHistory?: boolean,
    forceRequestPage = false,
  ): void {
    this.dateNow = new Date();
    this.uiSrv.loadingStateChanged.next(true);
    this.ordersListChanged.next([]);

    let httpParams = new HttpParams();

    if (this.getListSub) {
      this.getListSub.unsubscribe();
    }

    if (request.pageSize && request.page) {
      httpParams = httpParams
        .append('pageSize', request.pageSize.toString())
        // .append("page", request.query !== this.lastQuery ? "1" : request.page.toString());
        .append(
          'page',
          request.query !== this.lastQueryObj && !forceRequestPage
            ? '1'
            : request.page.toString(),
        );
    }

    const filtersQuery = [];

    if (request.query) {
      const filters = request.query;

      if (filters.identifier) {
        filtersQuery.push(`identifier==${filters.identifier}`);
      }

      if (filters.status) {
        if (filters.status !== 'all') {
          filtersQuery.push(`status==${filters.status}`);
        }
      }

      if (filters.name) {
        filtersQuery.push(`name@=${filters.name}`);
      }

      if (filters.type) {
        if (filters.type !== 'all') {
          filtersQuery.push(`type==${filters.type}`);
        }
      }
      if (filters.driver) {
        filtersQuery.push(`driver==${filters.driver}`);
      }
      if (filters.client) {
        filtersQuery.push(`client@=${filters.client}`);
      }
      this.pushSiteFilter(filters, filtersQuery);
      if (filters.range?.fromStartDate) {
        filtersQuery.push(
          `startDate>=${moment(new Date(filters.range.fromStartDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
        );
      }
      if (filters.range?.toStartDate) {
        filtersQuery.push(
          `startDate<=${moment(new Date(filters.range.toStartDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
        );
      }
      if (filters.createdBy) {
        if (typeof filters.createdBy !== 'string') {
          let createdBy = filters.createdBy.username;
          if (createdBy === 'Système') {
            filtersQuery.push(`isCreatedBySystem`);
          } else if (createdBy === 'Tous') {
          } else {
            filtersQuery.push(`createdBy==${createdBy}`);
          }
        }
      }
      if (filters.urgency) {
        filtersQuery.push(`urgency@=${filters.urgency}`);
      }
      if (filters.sender) {
        filtersQuery.push(`sender@=${filters.sender}`);
      }
      if (filters.receiver) {
        filtersQuery.push(`receiver@=${filters.receiver}`);
      }
      if (filters.includeDeleted) {
        httpParams = httpParams.append('includeDeleted', 'true');
      }
      if (!filters.includeFutureTour) {
        httpParams = httpParams.append(
          'tourMaxStartDate',
          moment(new Date()).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
        );
      }
    }

    if (request.sort) {
      httpParams = httpParams.append('sorts', request.sort);
    } else {
      // sort par défaut pour l'optimisation de requêtes
      httpParams = httpParams.append('sorts', 'startDate');
    }

    if (isHistory) {
      if (filtersQuery.length === 0) {
        filtersQuery.push(`status==16`);
      } else if (!filtersQuery.includes('status'))
        filtersQuery.push(`status==16|32`);
    } else {
      filtersQuery.push(`status!=16,status!=32`);
    }

    if (filtersQuery.length > 0) {
      httpParams = httpParams.append('Filters', filtersQuery.join());
    }

    // this.lastQuery = request.query;
    this.lastQueryObj = request.query;

    this.getListSub = this.http
      .get(this.baseUrl, { params: httpParams, observe: 'response' })
      .subscribe((result: HttpResponse<Order[]>) => {
        this.uiSrv.loadingStateChanged.next(false);
        const pagination: Pagination = JSON.parse(
          result.headers.get('X-Pagination'),
        );
        this.orders = result.body;
        this.ordersListChanged.next([...this.orders]);
        this.paginationSubject.next({ ...pagination });
      });
  }

  getSearchOverall(filters): void {
    const filtersQuery = [];
    let httpParams = new HttpParams();

    if (filters.driver) {
      filtersQuery.push(`driver@=${filters.driver.username}`);
    }

    if (filters.globalFilter) {
      filtersQuery.push(`globalFilter@=${filters.globalFilter}`);
    }

    if (filters.scanCode) {
      filtersQuery.push(`scanCode==${filters.scanCode}`);
    }

    if (filters.productType) {
      filtersQuery.push(`productType==${filters.productType.id}`);
    }

    if (filters.site) {
      filtersQuery.push(`site@=${filters.site.name}`);
    }

    if (filters.kooveaTag) {
      filtersQuery.push(`tag@=${filters.tag}`);
    }

    if (filters.startDate) {
      filtersQuery.push(
        `startDate>=${moment(new Date(filters.startDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
      );
      filtersQuery.push(
        `startDate<=${moment(new Date(filters.startDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
      );
    }

    httpParams = httpParams.append('Filters', filtersQuery.join());

    this.getListSub = this.http
      .get(this.baseUrl, { params: httpParams, observe: 'response' })
      .subscribe((result: HttpResponse<Order[]>) => {
        this.uiSrv.loadingStateChanged.next(false);

        this.orders = result.body;
        this.ordersListChanged.next([...this.orders]);
      });
  }

  get(id: string): Observable<Order> {
    return this.http.get<Order>(`${this.baseUrl}/${id}`).pipe(
      map((orderFromApi) => {
        orderFromApi = Order.fromObject(orderFromApi);
        this.selectedOrder = orderFromApi;
        this.selectedOrderChanged.next(orderFromApi);
        return orderFromApi;
      }),
    );
  }

  getCounter(): Observable<Counter> {
    return this.http.get<Counter>(`${this.baseUrl}/counter`).pipe(
      map((counter) => {
        this.assignedToTreatSubject.next(counter.assignedToTreat);
        this.unassignedToTreatSubject.next(counter.unassignedToTreat);
        this.counterTriggered.next(true);

        return counter;
      }),
    );
  }

  getInternalComment(orderId: string): Observable<InternalComment> {
    return this.http
      .get<InternalComment>(`${this.baseUrl}/${orderId}/internal-comment`)
      .pipe(
        map((internalComment) => {
          return internalComment;
        }),
      );
  }

  getTemperatures(orderId: string): Observable<OrderStepTemperature[]> {
    return this.http.get<any>(`${this.baseUrl}/${orderId}/temperatures`).pipe(
      map((temperatures) => {
        return temperatures.steps ?? null;
      }),
    );
  }

  get selectedId(): string {
    return this.selectedOrder ? this.selectedOrder.id : null;
  }

  exportLetter(id: string): Observable<Blob> {
    this.uiSrv.loadingStateChanged.next(true);
    return this.http
      .get(`${this.baseUrl}/${id}/pdf`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  generateAllLettersPdf(filters: any) {
    let httpParams = new HttpParams();
    const filtersQuery = [];

    if (filters) {
      if (filters.identifier) {
        filtersQuery.push(`identifier==${filters.identifier}`);
      }

      if (filters.status) {
        if (filters.status !== 'all') {
          filtersQuery.push(`status==${filters.status}`);
        }
      }

      if (filters.name) {
        filtersQuery.push(`name@=${filters.name}`);
      }

      if (filters.type) {
        if (filters.type !== 'all') {
          filtersQuery.push(`type==${filters.type}`);
        }
      }
      if (filters.driver) {
        filtersQuery.push(`driver==${filters.driver}`);
      }
      if (filters.client) {
        filtersQuery.push(`client@=${filters.client}`);
      }
      this.pushSiteFilter(filters, filtersQuery);
      if (filters.range?.fromStartDate) {
        filtersQuery.push(
          `startDate>=${moment(new Date(filters.range.fromStartDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
        );
      }
      if (filters.range?.toStartDate) {
        filtersQuery.push(
          `startDate<=${moment(new Date(filters.range.toStartDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
        );
      }
      if (filters.createdBy) {
        if (typeof filters.createdBy !== 'string') {
          let createdBy = filters.createdBy.username;
          if (createdBy === 'Système') {
            filtersQuery.push(`isCreatedBySystem`);
          } else if (createdBy === 'Tous') {
          } else {
            filtersQuery.push(`createdBy==${createdBy}`);
          }
        }
      }
      if (filters.urgency) {
        filtersQuery.push(`urgency@=${filters.urgency}`);
      }
      if (filters.sender) {
        filtersQuery.push(`sender@=${filters.sender}`);
      }
      if (filters.receiver) {
        filtersQuery.push(`receiver@=${filters.receiver}`);
      }
      if (filters.includeDeleted) {
        httpParams = httpParams.append('includeDeleted', 'true');
      }

      if (filtersQuery.length === 0) {
        filtersQuery.push(`status==16`);
      } else if (!filtersQuery.includes('status'))
        filtersQuery.push(`status==16|32`);
    }

    if (filtersQuery.length > 0) {
      httpParams = httpParams.append('Filters', filtersQuery.join());
    }

    if (filters.site) {
      httpParams = httpParams.append('siteId', filters.site.id);
    }

    if (filters.range?.fromStartDate) {
      httpParams = httpParams.append(
        'startDate',
        moment(new Date(filters.range.fromStartDate))
          .startOf('day')
          .utc()
          .format('YYYY-MM-DD HH:mm:ss'),
      );
    }

    if (filters.range?.toStartDate) {
      httpParams = httpParams.append(
        'endDate',
        moment(new Date(filters.range.toStartDate))
          .endOf('day')
          .utc()
          .format('YYYY-MM-DD HH:mm:ss'),
      );
    }

    if (filters.status) {
      if (filters.status !== 'all') {
        httpParams = httpParams.append('status', filters.status);
      }
    }

    return this.http.post(`${this.baseUrl}/generate-all-pdf`, null, {
      params: httpParams,
      observe: 'response',
    });
  }

  exportExcel(orders?: string[], includePrices?: boolean, isSubcontractor?: boolean): Observable<Blob> {
    this.uiSrv.loadingStateChanged.next(true);

    let httpParams = new HttpParams();

    if (includePrices) {
      httpParams = httpParams.append('includePrices', 'true');
    }

    if (isSubcontractor) {
      httpParams = httpParams.append('isSubcontractor', 'true');
    }

    return this.http
      .get(`${this.baseUrl}/xlsx?ids=${orders}`, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  exportExcelAll(
    request: PaginationRequest,
    isHistory?: boolean,
    includePrices?: boolean,
    isSubcontractor?: boolean,
  ) {
    let httpParams = new HttpParams();

    if (includePrices) {
      httpParams = httpParams.append('includePrices', 'true');
    }

    if (isSubcontractor) {
      httpParams = httpParams.append('isSubcontractor', 'true');
    }

    const filtersQuery = [];

    if (request.query) {
      const filters = request.query;

      if (filters.identifier) {
        filtersQuery.push(`identifier==${filters.identifier}`);
      }
      if (filters.type) {
        if (filters.type !== 'all') {
          filtersQuery.push(`type==${filters.type}`);
        }
      }
      if (filters.status) {
        if (filters.status !== 'all') {
          filtersQuery.push(`status==${filters.status}`);
        }
      }
      if (filters.driver) {
        filtersQuery.push(`driver@=${filters.driver}`);
      }
      if (filters.client) {
        filtersQuery.push(`client@=${filters.client}`);
      }
      this.pushSiteFilter(filters, filtersQuery);

      if (filters.range?.fromStartDate) {
        filtersQuery.push(
          `startDate>=${moment(new Date(filters.range.fromStartDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
        );
      }
      if (filters.range?.toStartDate) {
        filtersQuery.push(
          `startDate<=${moment(new Date(filters.range.toStartDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
        );
      }

      if (filters.createdBy) {
        if (typeof filters.createdBy !== 'string') {
          let createdBy = filters.createdBy.username;
          if (createdBy === 'Système') {
            filtersQuery.push(`isCreatedBySystem`);
          } else {
            filtersQuery.push(`createdBy==${createdBy}`);
          }
        }
      }
      if (filters.urgency) {
        filtersQuery.push(`urgency@=${filters.urgency}`);
      }
      if (filters.sender) {
        filtersQuery.push(`sender@=${filters.sender}`);
      }
      if (filters.receiver) {
        filtersQuery.push(`receiver@=${filters.receiver}`);
      }
    }

    if (isHistory) {
      if (filtersQuery.length === 0) {
        filtersQuery.push(`status==16`);
      } else if (!filtersQuery.includes('status'))
        filtersQuery.push(`status==16|32`);
    } else {
      filtersQuery.push(`status!=16,status!=32`);
    }

    if (filtersQuery.length > 0) {
      httpParams = httpParams.append('Filters', filtersQuery.join());
    }

    this.uiSrv.loadingStateChanged.next(true);
    return this.http
      .get(`${this.baseUrl}/xlsx`, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  exportPreBilling(request: PaginationRequest, isSubcontractor?: boolean): Observable<Blob> {
    let httpParams = new HttpParams();

    if (isSubcontractor) {
      httpParams = httpParams.append('isSubcontractor', 'true');
    }

    const filtersQuery = [];

    if (request.query) {
      const filters = request.query;

      if (filters.identifier) {
        filtersQuery.push(`identifier==${filters.identifier}`);
      }

      if (filters.status) {
        if (filters.status !== 'all') {
          filtersQuery.push(`status==${filters.status}`);
        }
      }

      if (filters.name) {
        filtersQuery.push(`name@=${filters.name}`);
      }

      if (filters.type) {
        if (filters.type !== 'all') {
          filtersQuery.push(`type==${filters.type}`);
        }
      }

      if (filters.driver) {
        filtersQuery.push(`driver==${filters.driver}`);
      }

      if (filters.client) {
        filtersQuery.push(`client@=${filters.client}`);
      }

      this.pushSiteFilter(filters, filtersQuery);

      if (filters.range?.fromStartDate) {
        filtersQuery.push(
          `startDate>=${moment(new Date(filters.range.fromStartDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
        );
      }

      if (filters.range?.toStartDate) {
        filtersQuery.push(
          `startDate<=${moment(new Date(filters.range.toStartDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')}`,
        );
      }

      if (filters.createdBy) {
        if (typeof filters.createdBy !== 'string') {
          let createdBy = filters.createdBy.username;
          if (createdBy === 'Système') {
            filtersQuery.push(`isCreatedBySystem`);
          } else if (createdBy === 'Tous') {
            // Don't add filter for "Tous"
          } else {
            filtersQuery.push(`createdBy==${createdBy}`);
          }
        }
      }

      if (filters.urgency) {
        filtersQuery.push(`urgency@=${filters.urgency}`);
      }

      if (filters.sender) {
        filtersQuery.push(`sender@=${filters.sender}`);
      }

      if (filters.receiver) {
        filtersQuery.push(`receiver@=${filters.receiver}`);
      }

      if (filters.includeDeleted) {
        httpParams = httpParams.append('includeDeleted', 'true');
      }

      // Always filter for finished or cancelled orders if no status specified
      if (!filters.status || filters.status === 'all') {
        filtersQuery.push(`status==16|32`);
      }
    } else {
      // Default to finished or cancelled orders
      filtersQuery.push(`status==16|32`);
    }

    if (filtersQuery.length > 0) {
      httpParams = httpParams.append('filters', filtersQuery.join(','));
    }

    this.uiSrv.loadingStateChanged.next(true);
    return this.http
      .get(`${environment.apiUrl}pre-billing`, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  put(order: Order): Observable<Order> {
    return this.http.put<Order>(this.baseUrl, order).pipe(
      tap((orderFromApi) => {
        orderFromApi = Order.fromObject(orderFromApi);
        this.selectedOrderChanged.next(orderFromApi);
      }),
    );
  }

  changeTimeSpent(orderId: string, newTime: number): Observable<Order> {
    let object = {
      timeSpent: newTime,
    };
    return this.http
      .put<Order>(`${this.baseUrl}/${orderId}/time-spent`, object)
      .pipe(
        tap((orderFromApi) => {
          this.orders.map((order) => {
            if (order.id === orderId) {
              order.timeSpent = newTime;
            }
          });
          this.ordersListChanged.next([...this.orders]);
        }),
      );
  }

  modifySite(order: Order, newSite: Site): Observable<Order> {
    return this.http
      .put<Order>(`${this.baseUrl}/${order.id}/site`, newSite)
      .pipe(
        tap((orderFromApi) => {
          orderFromApi = Order.fromObject(orderFromApi);
          this.selectedOrderChanged.next(orderFromApi);
        }),
      );
  }

  changeStatus(orderId: string, form: any): Observable<Order> {
    return this.http.put<Order>(`${this.baseUrl}/${orderId}/status`, form).pipe(
      tap((orderFromApi) => {
        orderFromApi = Order.fromObject(orderFromApi);
        this.selectedOrderChanged.next(orderFromApi);
        this.uiSrv.showSnackbar('orders.statusChanged', true);

        this.getCounter().subscribe();
      }),
    );
  }

  post(mission: Order): Observable<Order> {
    return this.http.post<Order>(this.baseUrl, mission).pipe(
      tap(() => {
        this.getCounter().subscribe();
      }),
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      tap(() => {
        this.orders.map((order) => {
          if (order.id === id) {
            order.isDeleted = true;
          }
        });
        this.ordersListChanged.next([...this.orders]);

        this.getCounter().subscribe();
      }),
    );
  }

  assignDriver(idOrder: string, driver: User, com: string): any {
    const assignedDriver = {
      driverId: driver.id,
      comment: com,
    };
    return this.http
      .put<any>(`${this.baseUrl}/${idOrder}/driver`, assignedDriver)
      .pipe(
        tap(() => {
          this.orders = this.orders?.filter((m) => m.id !== idOrder) ?? [];
          this.ordersListChanged.next([...this.orders]);

          this.getCounter().subscribe();
        }),
      );
  }

  /** Le donneur d'ordre unique est prioritaire sur le groupe de donneurs d'ordre. */
  private pushSiteFilter(filters: any, filtersQuery: string[]): void {
    if (filters.site) {
      if (typeof filters.site !== 'string') {
        filtersQuery.push(`site==${filters.site.name}`);
      } else {
        filtersQuery.push(`site@=${filters.site}`);
      }
      return;
    }

    const siteIds: string[] = filters.siteGroup?.siteIds?.length
      ? filters.siteGroup.siteIds
      : (filters.siteGroup?.sites ?? []).map((site: any) => site.id);

    if (siteIds.length) {
      filtersQuery.push(`siteId==${siteIds.join('|')}`);
    }
  }

  private saveFile(res: HttpResponse<Blob>): Blob {
    FileSaver.saveAs(res.body, res.headers.get('filename'));
    this.uiSrv.loadingStateChanged.next(false);
    return res.body;
  }

  sendPdfMail(orderId: string, email: string): Observable<any> {
    let emailToSend = {
      email: email,
    };
    return this.http.post<any>(
      `${this.baseUrl}/${orderId}/pdf/send`,
      emailToSend,
    );
  }

  patch(orderId: string, newInfos): Observable<Order> {
    return this.http.patch<Order>(`${this.baseUrl}/${orderId}`, newInfos).pipe(
      tap((orderFromApi) => {
        orderFromApi = Order.fromObject(orderFromApi);
        this.selectedOrderChanged.next(orderFromApi);
        this.uiSrv.showSnackbar(
          'Infos de commande modifiée avec succès',
          false,
        );
      }),
    );
  }

  patchInternalComment(
    orderId: string,
    internalComment: InternalComment,
  ): Observable<InternalComment> {
    return this.http
      .patch<InternalComment>(
        `${this.baseUrl}/${orderId}/internal-comment`,
        internalComment,
      )
      .pipe(
        tap(() => {
          this.uiSrv.showSnackbar(
            'Commentaire interne modifié avec succès',
            false,
          );
        }),
      );
  }

  public downloadRoadmap({ id }: Order): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/${id}/roadmap`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  generateScansReport(clientId: string | null, startDate: Date, endDate: Date, siteGroupId?: string): Observable<Blob> {
    this.uiSrv.loadingStateChanged.next(true);

    let httpParams = new HttpParams();

    if (clientId) {
      httpParams = httpParams.set('clientId', clientId);
    }

    if (siteGroupId) {
      httpParams = httpParams.set('siteGroupId', siteGroupId);
    }

    httpParams = httpParams
      .set('startDate', moment(startDate).startOf('day').utc().toISOString())
      .set('endDate', moment(endDate).endOf('day').utc().toISOString());

    return this.http
      .get(`${this.baseUrl}/scans/csv`, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }
}
